import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { LucideAngularModule, CircleAlert, X, CheckCircle } from 'lucide-angular';

/**
 * TVG-124: Optional Lost reason modal launched from the event detail page
 * "Mark as Lost" button. Captures the operator's chosen reason from a short
 * drop-down; "Other" reveals a free-text textarea. Submitting with no choice
 * is allowed — the lifecycle doc treats the reason as a reporting nicety,
 * not a workflow gate.
 */
export interface MarkAsLostPayload {
  lost_reason?: string | null;
  lost_reason_other?: string | null;
}

@Component({
  selector: 'app-mark-as-lost-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent, LucideAngularModule],
  template: `
    @if (isOpen) {
      <div class="fixed inset-0 z-[1000] overflow-y-auto" data-testid="mark-as-lost-modal">
        <div class="flex min-h-screen items-center justify-center p-4">
          <div class="fixed inset-0 bg-black/50 transition-opacity" (click)="onCancel()"></div>

          <div class="relative z-10 w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-gray-900">
            <div class="flex items-start gap-4 p-6">
              <div class="flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                <lucide-icon [name]="CircleAlertIcon" [size]="22"></lucide-icon>
              </div>
              <div class="flex-1">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                  Mark event as Lost
                </h3>
                <p class="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  Optionally record why this prospect didn't convert. This is used for reporting only.
                </p>
              </div>
              <button
                class="text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-200"
                (click)="onCancel()"
                aria-label="Close Mark as Lost modal">
                <lucide-icon [name]="XIcon" [size]="18"></lucide-icon>
              </button>
            </div>

            <form [formGroup]="form" class="space-y-3 px-6 pb-2">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300" for="lost-reason">
                Reason (optional)
              </label>
              <select
                id="lost-reason"
                formControlName="reason"
                data-testid="lost-reason-select"
                class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white">
                <option value="">No reason given</option>
                @for (option of reasons; track option) {
                  <option [value]="option">{{ option }}</option>
                }
              </select>

              @if (form.controls.reason.value === otherReason) {
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300" for="lost-reason-other">
                  Tell us more
                </label>
                <textarea
                  id="lost-reason-other"
                  formControlName="other"
                  rows="3"
                  maxlength="500"
                  data-testid="lost-reason-other"
                  placeholder="Add a short note (max 500 characters)"
                  class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white">
                </textarea>
              }
            </form>

            <div class="mt-4 flex items-center justify-end gap-3 bg-gray-50 px-6 py-4 dark:bg-gray-800/60">
              <app-button
                [label]="'Cancel'"
                [style]="'secondary'"
                [icon]="XIcon"
                (click)="onCancel()">
              </app-button>
              <app-button
                [label]="submitting ? 'Marking…' : 'Mark as Lost'"
                [style]="'primary'"
                [icon]="CheckIcon"
                [disabled]="submitting"
                data-testid="mark-as-lost-submit"
                (click)="onConfirm()">
              </app-button>
            </div>
          </div>
        </div>
      </div>
    }
  `,
})
export class MarkAsLostModalComponent {
  @Input() isOpen = false;
  @Input() submitting = false;

  // Mirrors Event::LOST_REASONS on the PHP side.
  protected readonly reasons: string[] = [
    'Went with another venue',
    'Budget',
    'Date no longer available',
    'Lost contact / no response',
    'Other',
  ];
  protected readonly otherReason = 'Other';

  protected readonly form = new FormGroup({
    reason: new FormControl<string>(''),
    other: new FormControl<string>('', { nonNullable: true, validators: [Validators.maxLength(500)] }),
  });

  @Output() confirmed = new EventEmitter<MarkAsLostPayload>();
  @Output() cancelled = new EventEmitter<void>();

  protected readonly CircleAlertIcon = CircleAlert;
  protected readonly XIcon = X;
  protected readonly CheckIcon = CheckCircle;

  onConfirm(): void {
    if (this.submitting) {
      return;
    }
    const reason = this.form.controls.reason.value || null;
    const other = this.form.controls.other.value?.trim() || null;
    this.confirmed.emit({
      lost_reason: reason,
      lost_reason_other: reason === this.otherReason ? other : null,
    });
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  reset(): void {
    this.form.reset({ reason: '', other: '' });
  }
}
